import React, {
  useMemo, useRef, useState, useCallback,
} from 'react';

import type Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import {
  Group, Line, Circle, Label, Tag, Text,
} from 'react-konva';

import type { PolygonShapeProps } from '@vlabs/monokit/shared/types/regions';

import {
  POLYGON_METRICS, HIT_STROKE_WIDTH_PX, CURSORS, DEFAULT_COLOR, FILL_ALPHA_BASE, FILL_ALPHA_HOVER, DEFAULT_INNER_COLOR,
} from './constants';
import { setStageCursor } from './setStageCursor';
import { flatten, nearestTopPoint, setAlphaChannelInColor } from './utils';

// Гасим нативное всплытие mousedown/touchstart, чтобы событие не дошло до window-листенера карты
// (pigeon-maps слушает на window) и не стартовало панорамирование при редактировании точек.
const stopNativeBubbling = (e: KonvaEventObject<MouseEvent | TouchEvent>): void => {
  e.evt.stopPropagation();
};

export function PolygonShape({
  points,
  color = DEFAULT_COLOR,
  filled = true,
  label = '',
  selected = false,
  interactive = false,
  onSelect = undefined,
  onMoveVertex = undefined,
  onRemoveVertex = undefined,
  onInsertAtEdge = undefined,
  onTranslate = undefined,
  TSizeBounds,
  canDrag = false,
}: PolygonShapeProps): React.JSX.Element {
  const groupRef = useRef<Konva.Group | null>(null);
  const lineRef = useRef<Konva.Line | null>(null);

  const flattened = useMemo(() => flatten(points), [points]);
  const [hovered, setHovered] = useState(false);

  const strokeWidth = selected ? POLYGON_METRICS.strokeWidthHover : POLYGON_METRICS.strokeWidthDefault;
  const fillAlpha = hovered ? FILL_ALPHA_HOVER : FILL_ALPHA_BASE;
  const fillColor = filled ? setAlphaChannelInColor(color, fillAlpha) : 'transparent';
  const top = nearestTopPoint(points);
  const showLabel = Boolean(label && points.length >= 3);

  // список рёбер для hit-областей
  const edges = useMemo(() => {
    const n = points.length;
    if (n < 2) return [];

    return points.map((start, i) => {
      const endIndex = i === n - 1 ? 0 : i + 1;
      const end = points[endIndex];
      return {
        startIndex: i, endIndex, start, end,
      };
    });
  }, [points]);

  // drag всей фигуры (через группу)
  const onGroupDragStart = (e: KonvaEventObject<DragEvent>) => {
    if (e.target !== groupRef.current) return;
    setStageCursor(e, CURSORS.move);
  };

  const onGroupDragMove = useCallback((e: KonvaEventObject<DragEvent>) => {
    if (e.target !== groupRef.current) return;
    const node = e.target;

    const rect = node.getClientRect();
    const overRight = (rect.x + rect.width) - (TSizeBounds.width + TSizeBounds.x * 2);
    const overBottom = (rect.y + rect.height) - (TSizeBounds.height + TSizeBounds.y * 2);

    if (rect.x < 0) node.x(node.x() - rect.x);
    if (rect.y < 0) node.y(node.y() - rect.y);
    if (overRight > 0) node.x(node.x() - overRight);
    if (overBottom > 0) node.y(node.y() - overBottom);
  }, [TSizeBounds.width, TSizeBounds.height]);

  const onGroupDragEnd = (e: KonvaEventObject<DragEvent>) => {
    if (e.target !== groupRef.current) return;
    const node = e.target;
    const dx = node.x();
    const dy = node.y();
    node.position({ x: 0, y: 0 });
    setStageCursor(e, CURSORS.move);
    if (dx !== 0 || dy !== 0) onTranslate?.(dx, dy);
  };

  const handleVertexDragStart = (e: KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    setStageCursor(e, CURSORS.grab);
    groupRef.current?.draggable(false);
  };

  const handleVertexDragMove = (idx: number, e: KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    const node = e.target;

    if (node.x() < 0) node.x(0);
    if (node.x() > TSizeBounds.width) node.x(TSizeBounds.width);

    if (node.y() < 0) node.y(0);
    if (node.y() > TSizeBounds.height) node.y(TSizeBounds.height);

    onMoveVertex?.(idx, { x: node.x(), y: node.y() });
  };

  const handleVertexDragEnd = useCallback((e: KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    setStageCursor(e, CURSORS.grab);
    groupRef.current?.draggable(!!canDrag && !!selected);
  }, [canDrag, selected]);

  const onPolygonClick = (e: Konva.KonvaEventObject<MouseEvent>): void => {
    e.cancelBubble = true;
    setStageCursor(e, CURSORS.move);
    onSelect?.();
  };

  return (
    <Group
      draggable={Boolean(canDrag && selected && interactive)}
      name="polygon"
      onClick={onPolygonClick}
      onDragEnd={onGroupDragEnd}
      onDragMove={onGroupDragMove}
      onDragStart={onGroupDragStart}
      onMouseDown={stopNativeBubbling}
      onTouchStart={stopNativeBubbling}
      ref={groupRef}
    >
      {/* видимая линия/заливка */}
      <Line
        closed
        fill={fillColor}
        fillAfterStrokeEnabled
        listening
        name="line"
        onMouseEnter={(evt) => {
          setHovered(true); setStageCursor(evt, CURSORS.pointer);
        }}
        onMouseLeave={(evt) => {
          setHovered(false); setStageCursor(evt, CURSORS.default);
        }}
        opacity={1}
        perfectDrawEnabled={false}
        points={flattened}
        ref={lineRef}
        shadowForStrokeEnabled={false}
        stroke={color}
        strokeWidth={strokeWidth}
      />

      {/* невидимая hit-маска по площади полигона (работает даже без filled) */}
      {!!interactive && !!selected && (
        <Line
          closed
          fill="rgba(0,0,0,0.001)" // почти прозрачный, но «осязаемый» для hit-теста
          listening
          name="drag-hit"
          onMouseDown={(evt) => {
            if (canDrag) setStageCursor(evt, CURSORS.move);
          }}
          onMouseEnter={(evt) => {
            setHovered(true); if (canDrag) setStageCursor(evt, CURSORS.move);
          }}
          onMouseLeave={(evt) => {
            setHovered(false); setStageCursor(evt, CURSORS.default);
          }}
          onMouseUp={(evt) => {
            if (canDrag) setStageCursor(evt, CURSORS.move);
          }}
          points={flattened}
          stroke="transparent"
        />
      )}

      {/* hit-области вдоль рёбер для вставки новых точек */}
      {!!interactive && !!selected && edges.map((e) => (
        <Line
          hitStrokeWidth={HIT_STROKE_WIDTH_PX}
          key={`edge-${points[e.startIndex].id}-${points[e.endIndex].id}`}
          name="edge-hit"
          onClick={(ev: KonvaEventObject<MouseEvent>) => {
            const pos = ev.target.getStage()?.getPointerPosition();
            if (!pos) return;
            onInsertAtEdge?.(e.startIndex, { x: pos.x, y: pos.y });
          }}
          onMouseEnter={(evt) => setStageCursor(evt, CURSORS.draw)}
          onMouseLeave={(evt) => setStageCursor(evt, CURSORS.default)}
          points={[e.start.x, e.start.y, e.end.x, e.end.y]}
          stroke="transparent"
        />
      ))}

      {/* вершины */}
      {!!interactive && !!selected && points.map((p, idx) => (
        <Circle
          draggable
          fill={DEFAULT_INNER_COLOR}
          key={p.id}
          name="vertex"
          onDblClick={() => {
            if (points.length > 3) onRemoveVertex?.(idx);
          }}
          onDragEnd={handleVertexDragEnd}
          onDragMove={(e) => handleVertexDragMove(idx, e)}
          onDragStart={handleVertexDragStart}
          onMouseEnter={(e) => setStageCursor(e, CURSORS.grab)}
          onMouseLeave={(e) => setStageCursor(e, CURSORS.default)}
          radius={POLYGON_METRICS.vertexRadiusPx}
          stroke={color}
          strokeWidth={strokeWidth}
          x={p.x}
          y={p.y}
        />
      ))}

      {/* лейбл */}
      {!!showLabel && (
        <Label
          name="label"
          offsetX={28}
          x={top.x}
          y={top.y - 22}
        >
          <Tag
            cornerRadius={4}
            fill={color}
            opacity={0.9}
          />
          <Text
            fill={DEFAULT_INNER_COLOR}
            fontSize={12}
            padding={6}
            text={label}
          />
        </Label>
      )}
    </Group>
  );
}
